/**
 * RequestQuotePage — EVENTSFLO-PUBLIC-QUOTE-REQUEST-FORM-TO-AI-DRAFT-EMAIL-V1
 *
 * Public, no-auth page that collects a structured crew request and posts it
 * to /api/public/quote-request (which emails crew@icrewservices.com plus a
 * confirmation copy to the submitter).
 *
 * Intentionally lightweight: no rate-card calculation, no project creation.
 * The processing note explains that operational rules are applied later in
 * the EventsFlo pipeline.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { Loader2, Plus, Trash2, ArrowLeft, CheckCircle2, Send, Info, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

// EVENTSFLO-PUBLIC-QUOTE-REQUEST-FORM-COPY-AND-FIELD-UX-V1
// Event types: bilingual where useful, free-form supported via <datalist>.
const EVENT_TYPE_OPTIONS = [
  "Ballet",
  "Concert / indoors",
  "Corporate event",
  "Concert / festival",
  "Trade show / exhibition",
  "Wedding",
  "Brand activation",
  "Film / TV production",
  "Conference",
  "Private event",
  "Other",
];

// EVENTSFLO-PUBLIC-QUOTE-REQUEST-FORM-COPY-AND-FIELD-UX-V1
// Activity options with EN — FR combined labels. "Strike" is intentionally
// removed (legacy synonym of Take-down). The value stored in the payload is
// the full combined label so admin/inbox parsing can see both languages.
const ACTIVITY_OPTIONS = [
  "Set-up / Take-down — Montage / Démontage",
  "Set-up — Montage",
  "Take-down — Démontage",
  "Show call — Prestation spectacle / Show call",
  "Stagehand — Technicien plateau / Stagehand",
  "Other — Autre",
];

// EVENTSFLO-PUBLIC-QUOTE-REQUEST-FORM-COPY-AND-FIELD-UX-V1
// Crew role / task seed list — combobox via <datalist>, free-form allowed.
const CREW_ROLE_OPTIONS = [
  "Crew / Road",
  "Other",
];

// EVENTSFLO-PUBLIC-QUOTE-REQUEST-FORM-TO-AI-DRAFT-EMAIL-V1.1
// Implementation note: TVA, address fields, and Google Maps URL are added
// to support FUTURE Auto Client Create and improved venue/location matching.
// This form must NOT create clients or projects automatically yet.

const emptyShift = (defaultDate = "") => ({
  date: defaultDate || "",
  start_time: "00:00",
  end_time: "00:00",
  duration: "",
  duration_manual: false,
  // EVENTSFLO-PUBLIC-QUOTE-REQUEST-FORM-TO-AI-DRAFT-EMAIL-V1.1: track whether
  // the user has actually edited each time field. Auto-duration only runs once
  // BOTH have been touched, so the 00:00/00:00 defaults don't auto-fill 24h.
  start_touched: false,
  end_touched: false,
  activity: "",
  role: "",
  quantity: "",
  location: "",
  notes: "",
});

/**
 * Auto-calculate "Xh Ym" duration from HH:MM start/end strings.
 * If end < start, treat as overnight (next day).
 * If end == start, return "" (zero / unspecified).
 * Returns "" when either time is invalid/missing.
 */
function calcDuration(start, end) {
  const re = /^(\d{1,2}):(\d{2})$/;
  const ms = re.exec((start || "").trim());
  const me = re.exec((end || "").trim());
  if (!ms || !me) return "";
  const sm = parseInt(ms[1], 10) * 60 + parseInt(ms[2], 10);
  let em = parseInt(me[1], 10) * 60 + parseInt(me[2], 10);
  if (em === sm) return ""; // identical → unspecified
  if (em < sm) em += 24 * 60; // overnight
  const diff = em - sm;
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

function Section({ title, description, children, testId }) {
  return (
    <Card data-testid={testId} className="border-border bg-card">
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent className="space-y-3">{children}</CardContent>
    </Card>
  );
}

function Field({ label, children, required, hint }) {
  return (
    <div className="space-y-1">
      <Label className="text-sm">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export default function RequestQuotePage() {
  const [submitting, setSubmitting] = useState(false);
  const [sendingTemplate, setSendingTemplate] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    requester_name: "",
    requester_company: "",
    requester_email: "",
    requester_phone: "",
    existing_client: false,
    // EVENTSFLO-PUBLIC-QUOTE-REQUEST-FORM-TO-AI-DRAFT-EMAIL-V1.1: client/billing fields
    requester_tva_number: "",
    requester_address_line_1: "",
    requester_address_line_2: "",
    requester_city: "",
    requester_postcode: "",
    requester_country: "",
    project_name: "",
    event_type: "",
    venue: "",
    venue_address: "",
    google_maps_url: "",
    meeting_point: "",
    start_date: "",
    end_date: "",
    project_notes: "",
    on_site_same: false,
    on_site_name: "",
    on_site_phone: "",
    on_site_email: "",
    additional_notes: "",
  });
  const [shifts, setShifts] = useState([emptyShift()]);

  // EVENTSFLO-PUBLIC-QUOTE-REQUEST-FORM-TO-AI-DRAFT-EMAIL-V1.1
  // Date cascade: when project Start Date is set, default end_date if empty
  // and propagate to any shift whose date is empty. Manual edits are preserved.
  const set = (k, v) => {
    setForm((prev) => {
      const next = { ...prev, [k]: v };
      if (k === "start_date" && v) {
        if (!prev.end_date) next.end_date = v;
      }
      return next;
    });
    if (k === "start_date" && v) {
      setShifts((prev) => prev.map((s) => (s.date ? s : { ...s, date: v })));
    }
  };

  const setShift = (i, k, v) =>
    setShifts((prev) =>
      prev.map((s, idx) => {
        if (idx !== i) return s;
        const next = { ...s, [k]: v };
        if (k === "duration") {
          // user typed a manual override
          next.duration_manual = true;
        }
        if (k === "start_time") next.start_touched = true;
        if (k === "end_time") next.end_touched = true;
        // Only auto-calc when both times have been touched and there's no manual override
        const startTouched = next.start_touched;
        const endTouched = next.end_touched;
        if (
          (k === "start_time" || k === "end_time") &&
          !next.duration_manual &&
          startTouched &&
          endTouched
        ) {
          next.duration = calcDuration(next.start_time, next.end_time);
        }
        return next;
      }),
    );

  const addShift = () =>
    setShifts((prev) => [...prev, emptyShift(form.start_date)]);
  const removeShift = (i) => setShifts((prev) => prev.filter((_, idx) => idx !== i));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.requester_name.trim() || !form.requester_email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    if (!form.project_name.trim() && !form.venue.trim()) {
      toast.error("Please provide a project name or venue.");
      return;
    }
    if (shifts.length === 0) {
      toast.error("Please add at least one shift.");
      return;
    }
    setSubmitting(true);
    try {
      // EVENTSFLO-PUBLIC-QUOTE-REQUEST-FORM-COPY-AND-FIELD-UX-V1
      // Existing clients skip TVA / address setup fields — payload omits them
      // (sends null) so legacy admin/inbox renderers don't see stale strings.
      const isExisting = !!form.existing_client;
      const payload = {
        requester_name: form.requester_name.trim(),
        requester_company: form.requester_company.trim() || null,
        requester_email: form.requester_email.trim(),
        requester_phone: form.requester_phone.trim() || null,
        existing_client: isExisting ? "yes" : "no",
        // EVENTSFLO-PUBLIC-QUOTE-REQUEST-FORM-TO-AI-DRAFT-EMAIL-V1.1
        requester_tva_number: isExisting ? null : (form.requester_tva_number.trim() || null),
        requester_address_line_1: isExisting ? null : (form.requester_address_line_1.trim() || null),
        requester_address_line_2: isExisting ? null : (form.requester_address_line_2.trim() || null),
        requester_city: isExisting ? null : (form.requester_city.trim() || null),
        requester_postcode: isExisting ? null : (form.requester_postcode.trim() || null),
        requester_country: isExisting ? null : (form.requester_country.trim() || null),
        project_name: form.project_name.trim() || null,
        event_type: form.event_type || null,
        venue: form.venue.trim() || null,
        venue_address: form.venue_address.trim() || null,
        google_maps_url: form.google_maps_url.trim() || null,
        meeting_point: form.meeting_point.trim() || null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        project_notes: form.project_notes.trim() || null,
        on_site_contact: {
          same_as_requester: form.on_site_same,
          name: form.on_site_name.trim() || null,
          phone: form.on_site_phone.trim() || null,
          email: form.on_site_email.trim() || null,
        },
        shifts: shifts.map((s) => ({
          date: s.date || null,
          start_time: s.start_time || null,
          end_time: s.end_time || null,
          // Always compute final duration: explicit override wins; otherwise
          // recompute from start/end so the email body always carries it.
          duration:
            (s.duration && s.duration_manual ? s.duration : calcDuration(s.start_time, s.end_time)) ||
            s.duration ||
            null,
          activity: s.activity || null,
          role: s.role || null,
          quantity: s.quantity ? Number(s.quantity) : null,
          location: s.location || null,
          notes: s.notes || null,
        })),
        additional_notes: form.additional_notes.trim() || null,
      };
      const res = await api.post("/public/quote-request", payload);
      const data = res.data;
      if (data?.delivery_failures?.length) {
        toast.warning("Submitted, but some emails could not be delivered.");
      } else {
        toast.success("Request received. A copy has been emailed to you.");
      }
      setSuccess(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to submit.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendTemplate = async () => {
    if (!form.requester_email.trim()) {
      toast.error("Enter your email first to receive the template.");
      return;
    }
    setSendingTemplate(true);
    try {
      await api.post("/public/quote-request/template", {
        requester_email: form.requester_email.trim(),
        requester_name: form.requester_name.trim() || null,
      });
      toast.success("Template sent — check your inbox.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to send template.";
      toast.error(msg);
    } finally {
      setSendingTemplate(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-surface-base flex items-center justify-center p-4">
        <Card className="max-w-lg w-full" data-testid="quote-success-page">
          <CardContent className="p-8 text-center space-y-4">
            <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto" />
            <h1 className="text-2xl font-bold">Request received</h1>
            <p className="text-foreground">A copy has been emailed to you. Our team will review your request and get back to you shortly.</p>
            <div className="flex gap-2 justify-center pt-3">
              <Button asChild variant="outline" data-testid="quote-back-to-login">
                <Link to="/login">Back to login</Link>
              </Button>
              <Button
                onClick={() => {
                  setSuccess(false);
                  setShifts([emptyShift()]);
                }}
                data-testid="quote-submit-another"
              >
                Submit another
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-base px-4 py-8" data-testid="request-quote-page">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <Link
            to="/login"
            className="text-sm text-primary inline-flex items-center gap-1 hover:underline"
            data-testid="quote-back-link"
          >
            <ArrowLeft className="h-4 w-4" /> Back to login
          </Link>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleSendTemplate}
            disabled={sendingTemplate}
            data-testid="quote-send-template-btn"
          >
            {sendingTemplate ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <FileText className="h-4 w-4 mr-2" />
            )}
            Send me a template
          </Button>
        </div>

        <div>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="quote-page-title">
            Request a quote
          </h1>
          <p className="text-foreground mt-1">
            Tell us about your project and shifts. We'll review and reply with a quote.
          </p>
        </div>

        {/* Important processing note */}
        <Card className="border-amber-500/40 bg-amber-500/5">
          <CardContent className="p-4 flex gap-3 text-sm">
            <Info className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <p>
              Travel, meals, crew chief requirements, out-of-town rules, supplements, and other
              operational rules will be reviewed and added automatically during EventsFlo
              processing where applicable. Please only provide the operational details you know.
            </p>
          </CardContent>
        </Card>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* 1. Requester */}
          <Section title="Your details" testId="section-requester">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Your name" required>
                <Input
                  required
                  value={form.requester_name}
                  onChange={(e) => set("requester_name", e.target.value)}
                  data-testid="requester-name"
                />
              </Field>
              <Field label="Company">
                <Input
                  value={form.requester_company}
                  onChange={(e) => set("requester_company", e.target.value)}
                  data-testid="requester-company"
                />
              </Field>
              <Field label="Email" required>
                <Input
                  required
                  type="email"
                  value={form.requester_email}
                  onChange={(e) => set("requester_email", e.target.value)}
                  data-testid="requester-email"
                />
              </Field>
              <Field label="Phone">
                <Input
                  value={form.requester_phone}
                  onChange={(e) => set("requester_phone", e.target.value)}
                  data-testid="requester-phone"
                />
              </Field>
            </div>

            {/* EVENTSFLO-PUBLIC-QUOTE-REQUEST-FORM-COPY-AND-FIELD-UX-V1
                Existing client switch sits directly under the contact fields.
                When ON, TVA / address setup fields are hidden (not required). */}
            <div
              className="flex items-start justify-between gap-4 rounded-md border border-border bg-surface-elevated/40 p-3"
              data-testid="existing-client-row"
            >
              <div>
                <Label htmlFor="existing-client-switch" className="text-sm font-medium cursor-pointer">
                  Existing client
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Turn on if we've worked together before — skips TVA / address setup.
                </p>
              </div>
              <Switch
                id="existing-client-switch"
                checked={!!form.existing_client}
                onCheckedChange={(v) => set("existing_client", !!v)}
                data-testid="requester-existing-client-switch"
              />
            </div>

            {!form.existing_client && (
              <div
                className="grid sm:grid-cols-2 gap-3"
                data-testid="client-setup-fields"
              >
                {/* EVENTSFLO-PUBLIC-QUOTE-REQUEST-FORM-TO-AI-DRAFT-EMAIL-V1.1 */}
                <Field
                  label="TVA / VAT number"
                  hint="Optional — speeds up future client setup."
                >
                  <Input
                    value={form.requester_tva_number}
                    onChange={(e) => set("requester_tva_number", e.target.value)}
                    placeholder="e.g. FR12345678901"
                    data-testid="requester-tva-number"
                  />
                </Field>
                <div />
                <Field label="Address line 1">
                  <Input
                    value={form.requester_address_line_1}
                    onChange={(e) => set("requester_address_line_1", e.target.value)}
                    data-testid="requester-address-line-1"
                  />
                </Field>
                <Field label="Address line 2">
                  <Input
                    value={form.requester_address_line_2}
                    onChange={(e) => set("requester_address_line_2", e.target.value)}
                    data-testid="requester-address-line-2"
                  />
                </Field>
                <Field label="City">
                  <Input
                    value={form.requester_city}
                    onChange={(e) => set("requester_city", e.target.value)}
                    data-testid="requester-city"
                  />
                </Field>
                <Field label="Postcode">
                  <Input
                    value={form.requester_postcode}
                    onChange={(e) => set("requester_postcode", e.target.value)}
                    data-testid="requester-postcode"
                  />
                </Field>
                <Field label="Country">
                  <Input
                    value={form.requester_country}
                    onChange={(e) => set("requester_country", e.target.value)}
                    placeholder="e.g. France"
                    data-testid="requester-country"
                  />
                </Field>
              </div>
            )}
          </Section>

          {/* 2. Project */}
          <Section title="Project" description="Provide a project name or venue (at least one)" testId="section-project">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Project name">
                <Input
                  value={form.project_name}
                  onChange={(e) => set("project_name", e.target.value)}
                  data-testid="project-name"
                />
              </Field>
              <Field label="Event type" hint="Pick from the list or type your own.">
                <Input
                  list="event-type-options"
                  value={form.event_type}
                  onChange={(e) => set("event_type", e.target.value)}
                  placeholder="e.g. Ballet, Concert / indoors, Other"
                  data-testid="event-type"
                />
                <datalist id="event-type-options">
                  {EVENT_TYPE_OPTIONS.map((t) => (
                    <option key={t} value={t} />
                  ))}
                </datalist>
              </Field>
              <Field label="Venue / location">
                <Input
                  value={form.venue}
                  onChange={(e) => set("venue", e.target.value)}
                  data-testid="venue"
                />
              </Field>
              <Field label="Venue address">
                <Input
                  value={form.venue_address}
                  onChange={(e) => set("venue_address", e.target.value)}
                  data-testid="venue-address"
                />
              </Field>
              {/* EVENTSFLO-PUBLIC-QUOTE-REQUEST-FORM-TO-AI-DRAFT-EMAIL-V1.1 */}
              <Field
                label="Google Maps URL / location link"
                hint="Helps us match the exact venue/location later."
              >
                <Input
                  type="url"
                  value={form.google_maps_url}
                  onChange={(e) => set("google_maps_url", e.target.value)}
                  placeholder="https://maps.google.com/..."
                  data-testid="project-google-maps-url"
                />
              </Field>
              <Field label="Meeting point">
                <Input
                  value={form.meeting_point}
                  onChange={(e) => set("meeting_point", e.target.value)}
                  data-testid="meeting-point"
                />
              </Field>
            </div>
            {/* EVENTSFLO-PUBLIC-QUOTE-REQUEST-FORM-COPY-AND-FIELD-UX-V1
                Dates share one row on >=sm and stack on mobile. */}
            <div className="grid sm:grid-cols-2 gap-3" data-testid="project-dates-row">
              <Field label="Start date">
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => set("start_date", e.target.value)}
                  data-testid="start-date"
                />
              </Field>
              <Field label="End date">
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => set("end_date", e.target.value)}
                  data-testid="end-date"
                />
              </Field>
            </div>
            <Field label="Project notes">
              <Textarea
                rows={3}
                value={form.project_notes}
                onChange={(e) => set("project_notes", e.target.value)}
                data-testid="project-notes"
              />
            </Field>
          </Section>

          {/* 3. On-site contact */}
          <Section title="On-site contact" testId="section-onsite-contact">
            <div className="flex items-center gap-2">
              <Checkbox
                id="on-site-same"
                checked={form.on_site_same}
                onCheckedChange={(v) => set("on_site_same", Boolean(v))}
                data-testid="onsite-same"
              />
              <Label htmlFor="on-site-same" className="cursor-pointer">
                Same as requester
              </Label>
            </div>
            {!form.on_site_same && (
              <div className="grid sm:grid-cols-3 gap-3">
                <Field label="Name">
                  <Input
                    value={form.on_site_name}
                    onChange={(e) => set("on_site_name", e.target.value)}
                    data-testid="onsite-name"
                  />
                </Field>
                <Field label="Phone">
                  <Input
                    value={form.on_site_phone}
                    onChange={(e) => set("on_site_phone", e.target.value)}
                    data-testid="onsite-phone"
                  />
                </Field>
                <Field label="Email">
                  <Input
                    type="email"
                    value={form.on_site_email}
                    onChange={(e) => set("on_site_email", e.target.value)}
                    data-testid="onsite-email"
                  />
                </Field>
              </div>
            )}
          </Section>

          {/* 4. Shifts */}
          <Section
            title="Shifts"
            description="Add one block per shift. Leave times blank if unknown."
            testId="section-shifts"
          >
            <div className="space-y-4">
              {shifts.map((shift, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-border p-4 space-y-3 bg-surface-elevated/40"
                  data-testid={`shift-${i}`}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">Shift {i + 1}</h3>
                    {shifts.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeShift(i)}
                        data-testid={`shift-remove-${i}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Date">
                      <Input
                        type="date"
                        value={shift.date}
                        onChange={(e) => setShift(i, "date", e.target.value)}
                        data-testid={`shift-${i}-date`}
                      />
                    </Field>
                    <div className="grid grid-cols-3 gap-2">
                      <Field label="Start">
                        <Input
                          type="time"
                          value={shift.start_time}
                          onChange={(e) => setShift(i, "start_time", e.target.value)}
                          data-testid={`shift-${i}-start`}
                        />
                      </Field>
                      <Field label="End">
                        <Input
                          type="time"
                          value={shift.end_time}
                          onChange={(e) => setShift(i, "end_time", e.target.value)}
                          data-testid={`shift-${i}-end`}
                        />
                      </Field>
                      <Field label="Duration" hint="Auto from times — editable">
                        <Input
                          placeholder="e.g. 4h 30m"
                          value={shift.duration}
                          onChange={(e) => setShift(i, "duration", e.target.value)}
                          data-testid={`shift-${i}-duration`}
                        />
                      </Field>
                    </div>
                    <Field label="Activity" hint="Pick from the list or type your own.">
                      <Input
                        list={`shift-${i}-activity-options`}
                        value={shift.activity}
                        onChange={(e) => setShift(i, "activity", e.target.value)}
                        placeholder="e.g. Set-up — Montage, Stagehand, …"
                        data-testid={`shift-${i}-activity`}
                      />
                      <datalist id={`shift-${i}-activity-options`}>
                        {ACTIVITY_OPTIONS.map((a) => (
                          <option key={a} value={a} />
                        ))}
                      </datalist>
                    </Field>
                    <Field label="Crew role / task" hint="Pick from the list or type your own.">
                      <Input
                        list={`shift-${i}-role-options`}
                        value={shift.role}
                        onChange={(e) => setShift(i, "role", e.target.value)}
                        placeholder="e.g. Crew / Road, Other"
                        data-testid={`shift-${i}-role`}
                      />
                      <datalist id={`shift-${i}-role-options`}>
                        {CREW_ROLE_OPTIONS.map((r) => (
                          <option key={r} value={r} />
                        ))}
                      </datalist>
                    </Field>
                    <Field label="Number of crew">
                      <Input
                        type="number"
                        min={1}
                        step={1}
                        inputMode="numeric"
                        value={shift.quantity}
                        onChange={(e) => setShift(i, "quantity", e.target.value)}
                        className="w-20 max-w-full"
                        data-testid={`shift-${i}-quantity`}
                      />
                    </Field>
                    <Field label="Location / area">
                      <Input
                        value={shift.location}
                        onChange={(e) => setShift(i, "location", e.target.value)}
                        data-testid={`shift-${i}-location`}
                      />
                    </Field>
                  </div>
                  <Field label="Notes / special instructions">
                    <Textarea
                      rows={2}
                      value={shift.notes}
                      onChange={(e) => setShift(i, "notes", e.target.value)}
                      data-testid={`shift-${i}-notes`}
                    />
                  </Field>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={addShift}
                data-testid="shift-add-btn"
              >
                <Plus className="h-4 w-4 mr-1" /> Add another shift
              </Button>
            </div>
          </Section>

          {/* 5. Additional info */}
          <Section title="Additional information (optional)" testId="section-additional">
            <Field
              label="Equipment / vehicle / access / parking / security"
              hint="Anything else useful for our team to know."
            >
              <Textarea
                rows={4}
                value={form.additional_notes}
                onChange={(e) => set("additional_notes", e.target.value)}
                data-testid="additional-notes"
              />
            </Field>
          </Section>

          <div className="flex flex-col sm:flex-row sm:justify-end gap-3 pt-2">
            <Button
              type="submit"
              disabled={submitting}
              className="bg-primary"
              data-testid="quote-submit-btn"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Send request
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
